// 认证和权限管理工具

/**
 * 检查用户是否已登录（基于 JWT Token）
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');
  return !!token && !!userId;
};

/**
 * 获取 JWT Token
 */
export const getToken = () => {
  return localStorage.getItem('token');
};

/**
 * 获取当前登录用户ID
 */
export const getCurrentUserId = () => {
  return localStorage.getItem('userId');
};

/**
 * 获取当前登录用户名
 */
export const getCurrentUserName = () => {
  return localStorage.getItem('userName');
};

/**
 * 获取当前用户角色ID
 */
export const getCurrentUserRole = () => {
  const roleId = localStorage.getItem('userRole');
  return roleId ? parseInt(roleId) : null;
};

/**
 * 保存登录信息（含 JWT Token）
 */
export const saveLoginInfo = (token, userId, userName, roleId) => {
  if (token) {
    localStorage.setItem('token', token);
  }
  localStorage.setItem('userId', userId);
  localStorage.setItem('userName', userName);
  localStorage.setItem('userRole', roleId);
  
  // 触发用户信息更新事件
  window.dispatchEvent(new Event('userInfoUpdated'));
};

/**
 * 清除登录信息（含 Token）
 */
export const clearLoginInfo = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('userId');
  localStorage.removeItem('userName');
  localStorage.removeItem('userRole');
};

/**
 * 角色定义
 */
export const ROLES = {
  ADMIN: 1,        // 管理员
  PURCHASER: 2,    // 采购员
  SALESPERSON: 3,  // 销售员
  TECHNICIAN: 4,   // 维修技术员
};

/**
 * localStorage 存储键：角色管理页面保存的自定义权限
 */
const PERMISSIONS_STORAGE_KEY = 'customRolePermissions';

/**
 * 默认角色权限映射（localStorage 无自定义配置时使用）
 */
const DEFAULT_ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: [
    '/',
    '/products',
    '/product-categories',
    '/suppliers',
    '/customers',
    '/purchase-orders',
    '/purchase-return-list',
    '/purchase-return-create',
    '/sales-orders',
    '/inventory',
    '/inventory-alert',
    '/reports',
    '/staff',
    '/roles',
    '/system-params',
    '/user-profile',
    '/cash-flows',
    '/repair-orders',
    '/repair-customers',
    '/repair-settlements',
    '/repair-stats',
  ],
  [ROLES.PURCHASER]: [
    '/',
    '/products',
    '/product-categories',
    '/suppliers',
    '/purchase-orders',
    '/purchase-return-list',
    '/purchase-return-create',
    '/inventory',
    '/inventory-alert',
    '/user-profile',
  ],
  [ROLES.SALESPERSON]: [
    '/',
    '/products',
    '/product-categories',
    '/customers',
    '/sales-orders',
    '/inventory',
    '/inventory-alert',
    '/reports',
    '/user-profile',
  ],
  [ROLES.TECHNICIAN]: [
    '/',
    '/repair-orders',
    '/user-profile',
  ],
};

/**
 * 动态获取角色权限：优先从 localStorage（角色管理页面编辑后保存）读取，
 * 无则回退到默认配置。管理员始终拥有全部权限。
 */
export const getRolePermissionsFromStorage = (roleId) => {
  if (roleId === ROLES.ADMIN) {
    return null; // null 表示全部权限
  }
  try {
    const stored = localStorage.getItem(PERMISSIONS_STORAGE_KEY);
    if (stored) {
      const all = JSON.parse(stored);
      if (all[roleId]) return all[roleId];
    }
  } catch { /* ignore */ }
  return DEFAULT_ROLE_PERMISSIONS[roleId] || [];
};

/**
 * 保留旧导出名供向后兼容（返回默认权限，不含 localStorage 动态配置）
 */
export const ROLE_PERMISSIONS = DEFAULT_ROLE_PERMISSIONS;

/**
 * 检查用户是否有访问指定路径的权限
 */
export const hasPermission = (path) => {
  if (!isAuthenticated()) {
    return false;
  }

  const roleId = getCurrentUserRole();
  if (!roleId) {
    return false;
  }

  // 用户个人中心对所有角色默认开放
  if (path === '/user-profile') {
    return true;
  }

  // 管理员拥有所有权限
  if (roleId === ROLES.ADMIN) {
    return true;
  }

  // 动态获取角色权限（支持角色管理页面编辑后即时生效）
  const permissions = getRolePermissionsFromStorage(roleId);
  if (!permissions) return true; // null = 全部权限

  // 精确匹配
  if (permissions.includes(path)) {
    return true;
  }

  // 检查是否是子路径（例如 /products/create 应该匹配 /products）
  return permissions.some(permission => {
    if (permission === '/') return false;
    return path.startsWith(permission);
  });
};

/**
 * 获取角色名称
 */
export const getRoleName = (roleId) => {
  const roleMap = {
    [ROLES.ADMIN]: '管理员',
    [ROLES.PURCHASER]: '采购员',
    [ROLES.SALESPERSON]: '销售员',
    [ROLES.TECHNICIAN]: '维修技术员',
  };
  return roleMap[roleId] || '未知角色';
};

// ============================================================
// 维修门户客户登录态（与内部员工登录态完全隔离）
// ============================================================
const PORTAL_TOKEN_KEY = 'portal_token';
const PORTAL_CUSTOMER_KEY = 'portal_customer';

export const getPortalToken = () => localStorage.getItem(PORTAL_TOKEN_KEY);

export const isPortalAuthenticated = () => !!localStorage.getItem(PORTAL_TOKEN_KEY);

export const savePortalLogin = (token, customer) => {
  localStorage.setItem(PORTAL_TOKEN_KEY, token);
  localStorage.setItem(PORTAL_CUSTOMER_KEY, JSON.stringify(customer || {}));
};

export const getPortalCustomer = () => {
  try {
    return JSON.parse(localStorage.getItem(PORTAL_CUSTOMER_KEY) || '{}');
  } catch {
    return {};
  }
};

export const clearPortalLogin = () => {
  localStorage.removeItem(PORTAL_TOKEN_KEY);
  localStorage.removeItem(PORTAL_CUSTOMER_KEY);
};

/**
 * 根据角色过滤菜单项
 */
export const filterMenuByRole = (menuItems, roleId) => {
  if (!roleId) {
    return [];
  }

  // 管理员看到所有菜单
  if (roleId === ROLES.ADMIN) {
    return menuItems;
  }

  // 动态获取角色权限（支持角色管理页面编辑后即时生效）
  const permissions = getRolePermissionsFromStorage(roleId) || [];
  
  const filtered = menuItems.filter(item => {
    // 如果是父菜单（有children），根据子菜单权限决定是否显示
    if (item.children && item.children.length > 0) {
      // 过滤子菜单
      const filteredChildren = item.children.filter(child => {
        return permissions.includes(child.key);
      });
      
      // 如果有可访问的子菜单，保留父菜单
      return filteredChildren.length > 0;
    }
    
    // 普通菜单项检查权限：使用精确匹配
    return permissions.includes(item.key);
  }).map(item => {
    // 如果有子菜单，过滤子菜单
    if (item.children && item.children.length > 0) {
      return {
        ...item,
        children: item.children.filter(child => {
          return permissions.includes(child.key);
        }),
      };
    }
    return item;
  });
  
  return filtered;
};
